<template>
  <div
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    style="background: rgba(0, 0, 0, 0.5)"
    @click.self="onClose"
  >
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("Report history") }}</h5>
          <button
            type="button"
            class="btn-close"
            :aria-label="$t('Close')"
            @click="onClose"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="isLoading" class="text-center p-3">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div v-else-if="archivedReports?.items.length" class="table-responsive">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th>{{ $t("Name") }}</th>
                  <th>{{ $t("Type") }}</th>
                  <th>{{ $t("Generated at") }}</th>
                  <th>{{ $t("Actions") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="archivedReport in archivedReports?.items" :key="archivedReport.id">
                  <td>{{ archivedReport.name }}</td>
                  <td>{{ $t(`report_type.${archivedReport.type}`) }}</td>
                  <td>{{ moment(archivedReport.generatedAt).format("YYYY-MM-DD HH:mm") }}</td>
                  <td>
                    <b-button
                      type="button"
                      variant="outline-primary"
                      size="sm"
                      @click="onArchivedReportDownloadClick(archivedReport.id)"
                    >
                      <i class="fas fa-download me-1"></i> {{ $t("Download") }}
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center p-3 text-muted">
            {{ $t("No history records found") }}
          </div>

          <div v-if="showPagination" class="mt-4 d-flex justify-content-center">
            <b-pagination
              v-model="currentPage"
              :total-rows="archivedReports?.totalResults ?? 0"
              :per-page="archivedReports?.perPage ?? 10"
              align="center"
              @update:model-value="onPageChange"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="onClose">
            {{ $t("Close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import moment from "moment";
import axios from "axios";
import { API_URL } from "@/config";
import downloadFile from "@/utils/downloadFile";
import { useGetArchivedReportsQuery } from "../queries/useGetArchivedReportsQuery";

const store = useStore();
const router = useRouter();

const props = defineProps({
  automatedReportId: {
    type: String,
    required: true,
  },
});

const currentPage = ref(1);

const { data: archivedReports, isLoading } = useGetArchivedReportsQuery(
  props.automatedReportId,
  currentPage,
);

const showPagination = computed(() => {
  return (archivedReports.value?.totalResults ?? 0) > (archivedReports.value?.perPage ?? 10);
});

const onClose = () => {
  router.push({ name: "automatedReports.list" });
};

const onPageChange = (page: number) => {
  currentPage.value = page;
};

const onArchivedReportDownloadClick = async (archivedReportId: string) => {
  try {
    const downloadUrl = await store.dispatch(
      "automatedReports/getArchivedReportDownloadUrl",
      archivedReportId,
    );

    if (downloadUrl.includes(API_URL)) {
      const response = await axios.get(downloadUrl, { responseType: "blob" });

      downloadFile(response);

      return;
    }

    window.open(downloadUrl, "_blank");
  } catch (e) {
    console.error(e);
  }
};
</script>
